/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, EventEmitter, Input, Output } from '@angular/core';
import { isLendersScheduleCode } from '@gh/builder-shared-data';
import {
  AbstractSnapshotFormComponent,
  FormState,
  FormStateBuilder,
  FormStateSnapshot,
  IS_REGION_US,
  NumberTypes,
  whenAuOrNzRegion,
  whenCreateOrEditMode,
} from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { getControlValue, isViewFormMode, setControlValue } from '@gh/core-ui';
import { find, isNil } from 'lodash';
import { JobDrawsDetailsInfo, JobDrawsDetailsRule } from '../../../resources/job-details-tab';

@Component({
  standalone: false,
  selector: 'gh-job-details-draws-form',
  templateUrl: 'job-details-draws-form.component.html',
  styleUrls: ['job-details-draws-form.component.scss'],
})
export class JobDetailsDrawsFormComponent extends AbstractSnapshotFormComponent<JobDrawsDetailsInfo> {
  @Input() drawRuleId: ID;
  @Input() drawRules: JobDrawsDetailsRule[];
  @Input() hasInvoice: boolean;

  @Output() drawRuleIdChange = new EventEmitter<ID>();

  get contractAmountLabel(): string {
    return IS_REGION_US ? 'lbl_contract_amount_dollar' : 'lbl_ex_gst_dollar';
  }

  get variationsAmountLabel(): string {
    return IS_REGION_US ? 'lbl_job_change_orders_dollar' : 'lbl_ex_gst_dollar';
  }

  get totalAmountLabel(): string {
    return IS_REGION_US ? 'lbl_total_dollar' : 'lbl_ex_gst_dollar';
  }

  get isDrawRuleDisabled(): boolean {
    if (isViewFormMode(this.mode)) {
      return true;
    }

    const drawRule = find(this.drawRules, { id: this.drawRuleId });
    const isLendersSchedule = drawRule ? isLendersScheduleCode(drawRule.code) : false;
    return isLendersSchedule ? this.hasInvoice : false;
  }

  constructor(private formStateBuilder: FormStateBuilder) {
    super();
  }

  onContractAmountChanged(): void {
    const contractAmount = getControlValue(this.form.group, 'contractAmount');
    if (isNil(contractAmount)) {
      return;
    }

    setControlValue(this.form.group, 'isContractAmountManual', true);
    setControlValue(this.form.group, 'isContractAmountWithGstManual', false);
  }

  onContractAmountWithGstChanged(): void {
    const contractAmountWithGst = getControlValue(this.form.group, 'contractAmountWithGst');
    if (isNil(contractAmountWithGst)) {
      return;
    }

    setControlValue(this.form.group, 'isContractAmountManual', false);
    setControlValue(this.form.group, 'isContractAmountWithGstManual', true);
  }

  protected buildForm(snapshot: FormStateSnapshot<JobDrawsDetailsInfo>): FormState<JobDrawsDetailsInfo> {
    return this.formStateBuilder.buildPlain({
      snapshot,
      mode: this.mode,
      enabled: whenCreateOrEditMode(),
      children: {
        contractAmount: { required: true, type: NumberTypes.SignedPrice },
        contractAmountLenders: void 0,
        contractAmountWithGst: { visible: whenAuOrNzRegion(), required: true, type: NumberTypes.SignedPrice },
        contractDrawnAmount: { enabled: false, type: NumberTypes.SignedPrice },
        contractToBe: { enabled: false, type: NumberTypes.SignedPrice },

        variationsAmount: { enabled: false, type: NumberTypes.SignedPrice },
        variationsAmountWithGst: { visible: whenAuOrNzRegion(), enabled: false, type: NumberTypes.SignedPrice },
        variationsDrawnAmount: { enabled: false, type: NumberTypes.SignedPrice },
        variationsToBe: { enabled: false, type: NumberTypes.SignedPrice },

        totalDrawnAmount: { enabled: false, type: NumberTypes.SignedPrice },
        totalAmountWithGst: { visible: whenAuOrNzRegion(), enabled: false, type: NumberTypes.SignedPrice },
        totalAmount: { enabled: false, type: NumberTypes.SignedPrice },
        totalToBe: { enabled: false, type: NumberTypes.SignedPrice },

        isContractAmountManual: void 0,
        isContractAmountWithGstManual: void 0,
      },
    });
  }
}
